import { useEffect, useMemo, useRef, useState } from 'react';
import { AddressStruct, AppliedPromo } from '../../types/common';
import { FREE_DELIVERY_MIN_TOTAL } from '../../lib/cartPage/config';
import { isFreeDeliveryZoneAddress } from '../../lib/cartPage/freeDelivery';
import { getCsrfToken } from '../../utils/helpers';
import { ProductCartItemWithSecondPromo } from './useCartPricing';

type SelectedDeliveryAddress = {
    raw: string;
    struct: AddressStruct | null;
    comment?: string;
} | null;
type UseShippingArgs = {
    authUser: any;
    selectedDeliveryAddress: SelectedDeliveryAddress;
    cartProducts: ProductCartItemWithSecondPromo[];
    weight: number;
    goodsPayable: number;
    appliedPromo: AppliedPromo | null;
    selectedDiscount: 'birthday' | 'bonus' | null;
    bonusEligible: number | null;
};

export function useShipping({
    // authUser,
    selectedDeliveryAddress,
    cartProducts,
    weight,
    goodsPayable,
    appliedPromo,
    selectedDiscount,
    bonusEligible,
}: UseShippingArgs) {
    /* -------- способ доставки -------- */
    const [shippingMethod, setShippingMethod] = useState<'pickup' | 'dpd'>('dpd');
    const [shippingCost, setShippingCost] = useState<number | null>(null);
    const [shippingError, setShippingError] = useState('');
    const selectedAddressRaw = useMemo(() => selectedDeliveryAddress?.raw?.trim() || '', [selectedDeliveryAddress]);
    const selectedAddressStruct = useMemo(() => selectedDeliveryAddress?.struct ?? null, [selectedDeliveryAddress]);
    // --- DTL (бесплатная доставка) ---
    const isFreeDeliveryZone = useMemo(
        () => isFreeDeliveryZoneAddress(selectedDeliveryAddress ? { address: selectedDeliveryAddress } : null),
        [selectedDeliveryAddress],
    );
    const isFreeDeliveryEligible = shippingMethod === 'dpd' && isFreeDeliveryZone && goodsPayable >= FREE_DELIVERY_MIN_TOTAL;
    const effectiveDeliveryMethod: 'pickup' | 'dpd' | 'dtl' = isFreeDeliveryEligible ? 'dtl' : shippingMethod;
    const effectiveShippingCost: number | null = isFreeDeliveryEligible ? 0 : shippingCost;
    /* -------- проверка валидности структурного адреса для тарифа -------- */
    const isStructAddressReady = () => {
        const a: AddressStruct | null = selectedAddressStruct;
        if (!a) return false;
        return /^\d{6}$/.test(a.postalCode || '') && !!a.city && !!a.street && !!a.house;
    };
    /* -------- дебаунс для расчёта -------- */
    const shippingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const shippingAbortRef = useRef<AbortController | null>(null);
    /* -------- расчёт стоимости доставки DPD -------- */
    useEffect(() => {
        if (shippingDebounceRef.current) {
            clearTimeout(shippingDebounceRef.current);
        }
        // Сброс при смене метода
        if (shippingMethod !== 'dpd') {
            if (shippingAbortRef.current) shippingAbortRef.current.abort();
            setShippingCost(null);
            setShippingError('');
            return;
        }
        // DTL: бесплатная доставка - DPD НЕ считаем
        if (isFreeDeliveryEligible) {
            if (shippingAbortRef.current) shippingAbortRef.current.abort();
            setShippingCost(0);
            setShippingError('');
            return;
        }
        // Проверяем готовность структурного адреса
        const ready = isStructAddressReady();
        if (!ready) {
            setShippingCost(null);
            setShippingError('');
            return;
        }
        // Планируем расчёт через 300 мс (debounce)
        shippingDebounceRef.current = setTimeout(async () => {
            if (shippingAbortRef.current) shippingAbortRef.current.abort();
            const ctrl = new AbortController();
            shippingAbortRef.current = ctrl;
            setShippingError('');
            setShippingCost(null);
            try {
                const csrf = await getCsrfToken();
                const goodsDeclared = goodsPayable;
                const parcels = cartProducts
                    .filter((it) => it.dimensions && it.dimensions.length && it.dimensions.width && it.dimensions.height)
                    .map((it) => ({
                        weight: it.weight,
                        length: it.dimensions!.length,
                        width: it.dimensions!.width,
                        height: it.dimensions!.height,
                        quantity: it.quantity,
                    }));
                const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/dpd/calculateShipping`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                    body: JSON.stringify({
                        address: selectedDeliveryAddress ? { raw: selectedDeliveryAddress.raw, struct: selectedDeliveryAddress.struct } : null,
                        weight,
                        declaredValue: goodsDeclared,
                        parcels: parcels.length ? parcels : undefined,
                    }),
                    signal: ctrl.signal,
                });
                if (!res.ok) {
                    let msg = 'Ошибка расчета доставки';
                    try {
                        const j = await res.json();
                        if (j?.error) msg = j.error;
                    } catch {}
                    setShippingError(msg);
                    setShippingCost(null);
                } else {
                    const data = await res.json();
                    if (typeof data.cost === 'number') {
                        setShippingCost(data.cost);
                        setShippingError('');
                    } else {
                        setShippingError('Некорректный ответ тарификатора');
                        setShippingCost(null);
                    }
                }
            } catch (e: any) {
                if (e?.name !== 'AbortError') {
                    console.error('Не удалось рассчитать доставку', e);
                    setShippingError('Ошибка расчета доставки');
                    setShippingCost(null);
                }
            }
        }, 300);
        return () => {
            if (shippingDebounceRef.current) clearTimeout(shippingDebounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        shippingMethod,
        selectedAddressRaw,
        selectedAddressStruct?.postalCode,
        selectedAddressStruct?.city,
        selectedAddressStruct?.street,
        selectedAddressStruct?.house,
        selectedAddressStruct?.block,
        selectedAddressStruct?.flat,
        selectedAddressStruct?.streetId,
        selectedAddressStruct?.dpdCityId,
        weight,
        appliedPromo,
        selectedDiscount,
        bonusEligible,
        goodsPayable,
        isFreeDeliveryEligible,
    ]);

    return {
        shippingMethod,
        setShippingMethod,
        shippingCost,
        shippingError,
        isFreeDeliveryZone,
        isFreeDeliveryEligible,
        effectiveDeliveryMethod,
        effectiveShippingCost,
    };
}
